"use client";

import React, { useState } from "react";

const services = [
  "Managed IT Services",
  "Infrastructure & Technology",
  "Network Solutions",
  "Cybersecurity",
  "Cloud & Microsoft 365",
  "Business Communications",
  "AI & Automation",
  "General Enquiry",
];

type FormState = "idle" | "loading" | "success" | "error";

export default function InlineContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [state, setState] = useState<FormState>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("loading");
    setErrorMsg("");

    try {
      const nameParts = form.name.trim().split(/\s+/);
      const payload = {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: form.email,
        company: form.company,
        service: form.service || "General Enquiry",
        message: form.message,
      };

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setState("success");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      setState("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const inputClass =
    "w-full px-4 py-3 text-[14px] font-body rounded-xl outline-none transition-all duration-200 placeholder:text-white/30 focus:border-l-[#5B21FF]";
  const inputStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderLeft: "2px solid transparent",
    color: "#F8FAFC",
  };

  if (state === "success") {
    return (
      <div className="rounded-2xl p-8 sm:p-10 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(20px)" }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(34,211,238,0.15)", border: "1px solid rgba(34,211,238,0.3)" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="font-heading font-700 text-[20px] mb-2" style={{ color: "#F8FAFC" }}>Message Sent</h3>
        <p className="text-[14px] font-body mb-6" style={{ color: "#94A3B8" }}>
          We&apos;ll get back to you within 2 hours during NZ business hours.
        </p>
        <button
          onClick={() => setState("idle")}
          className="text-[13px] font-heading font-500 transition-colors duration-200"
          style={{ color: "#60A5FA" }}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Background glow behind form */}
      <div
        className="absolute -inset-4 rounded-3xl pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 50%, rgba(91,33,255,0.10) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
    <form
      onSubmit={handleSubmit}
      className="relative rounded-2xl p-7 sm:p-9 overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(20px)",
      }}
    >
      {/* Gradient top border accent */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: "linear-gradient(90deg, #5B21FF 0%, #0066FF 50%, #00D4FF 100%)",
        }}
      />

      {/* Form heading */}
      <div className="mb-6">
        <h3 className="font-heading font-700 text-[18px] mb-1" style={{ color: "#F8FAFC" }}>
          Send us a message
        </h3>
        <p className="text-[13px] font-body" style={{ color: "rgba(255,255,255,0.5)" }}>
          We&apos;ll respond within 2 hours
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          name="name"
          placeholder="Name *"
          required
          value={form.name}
          onChange={handleChange}
          className={inputClass}
          style={inputStyle}
        />
        <input
          type="email"
          name="email"
          placeholder="Business Email *"
          required
          value={form.email}
          onChange={handleChange}
          className={inputClass}
          style={inputStyle}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          name="company"
          placeholder="Company"
          value={form.company}
          onChange={handleChange}
          className={inputClass}
          style={inputStyle}
        />
        <select
          name="service"
          value={form.service}
          onChange={handleChange}
          className={inputClass}
          style={{
            ...inputStyle,
            color: form.service ? "#F8FAFC" : "rgba(255,255,255,0.25)",
          }}
        >
          <option value="" disabled>
            Service of Interest
          </option>
          {services.map((s) => (
            <option key={s} value={s} style={{ background: "#111827", color: "#F8FAFC" }}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <textarea
        name="message"
        placeholder="Tell us about your project or IT needs..."
        rows={3}
        value={form.message}
        onChange={handleChange}
        className={`${inputClass} resize-none mb-4`}
        style={inputStyle}
      />

      {state === "error" && (
        <div className="text-[13px] font-body mb-3 px-3 py-2 rounded-lg" style={{ color: "#F87171", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)" }}>
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={state === "loading"}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-[15px] font-heading font-600 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
        style={{
          background: "linear-gradient(135deg, #5B21FF 0%, #0066FF 50%, #00D4FF 100%)",
          boxShadow: "0 4px 20px rgba(91,33,255,0.25)",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 30px rgba(91,33,255,0.3), 0 4px 20px rgba(91,33,255,0.25)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(91,33,255,0.25)"; }}
      >
        {state === "loading" ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Sending...
          </>
        ) : (
          <>
            Send Message
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </>
        )}
      </button>

      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-4">
        {["No obligation", "Response within 2 hours", "NZ-based team"].map((t) => (
          <span key={t} className="text-[11px] font-body" style={{ color: "rgba(148,163,184,0.45)" }}>
            {t}
          </span>
        ))}
      </div>
    </form>
    </div>
  );
}
